import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
  DataTable, InfoHint, Panel, StatusChip, ValueWithDelta,
  type DataTableColumn, type StatusTone,
} from '../../../../ui-kit';
import type { SimPath, SimResult, SimulatorPayload } from '../../../../types/plannerDashboard';
import { dollars, oneDecimal, percent, twoDecimal } from '../plannerFormat';

export interface SimulatorGoal {
  costPerSubmit: number;
  maxCostPerLead: number;
  maxAgents: number | null;
}

interface SimulatorPanelProps {
  payload: SimulatorPayload | null;
  loading?: boolean;
  error?: string | null;
}

type Row = { key: string; label: string; today: boolean; path: SimPath | null; result: SimResult };

/** A column heading with its explanation behind an info icon, as on the day table. */
function Heading({ label, info }: { label: string; info: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}
      <InfoHint title={info} label={`About ${label.toLowerCase()}`} />
    </Box>
  );
}

export default function SimulatorPanel({ payload, loading, error }: SimulatorPanelProps) {
  // The targets this answer was worked out for, not the ones in the boxes
  // above: those only count once someone presses Simulate.
  const ran = payload?.goal ?? null;
  const today = payload?.today ?? null;
  const paths = React.useMemo(() => payload?.paths ?? [], [payload]);

  // Open on the path that reaches the target with the fewest levers moved;
  // else the one that gets closest.
  const recommended = React.useMemo(() => {
    const reached = paths.filter((p) => p.reaches_goal);
    if (reached.length) return reached[0];
    const costed = paths.filter((p) => p.result.cost_per_submit !== null);
    if (costed.length) {
      return costed.reduce((a, b) =>
        ((b.result.cost_per_submit ?? Infinity) < (a.result.cost_per_submit ?? Infinity) ? b : a));
    }
    return paths[0] ?? null;
  }, [paths]);

  const rows: Row[] = today
    ? [
        { key: 'today', label: 'Today', today: true, path: null, result: today },
        ...paths.map((p) => ({ key: p.key, label: p.label, today: false, path: p, result: p.result })),
      ]
    : [];

  const status = (row: Row): { tone: StatusTone; label: string } => {
    if (row.today) return { tone: 'neutral', label: 'Where we are today' };
    if (row.path?.reaches_goal) return { tone: 'good', label: `Reaches ${dollars(ran?.cost_per_submit ?? null)}` };
    return { tone: 'warning', label: `Does not reach ${dollars(ran?.cost_per_submit ?? null)}` };
  };

  const vs = (row: Row, value: number | null, against: number | null, text: string, lowerIsBetter = false) =>
    row.today || value === null || against === null ? (
      text
    ) : (
      <ValueWithDelta value={value} against={against} lowerIsBetter={lowerIsBetter}>{text}</ValueWithDelta>
    );

  /** A rate the path has to reach, shown against where it stands today. */
  const rate = (row: Row, value: number, base: number | undefined, lifted: boolean) => {
    const text = percent(100 * value);
    if (row.today || !lifted || base === undefined) {
      return <Box component="span" sx={(t) => ({ color: row.today ? undefined : t.tokens.text.tertiary })}>{text}</Box>;
    }
    return <ValueWithDelta value={value} against={base}>{text}</ValueWithDelta>;
  };

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'path',
      header: <Heading label="Path" info={'Each row is one way we could reach the cost per submit you set above. A row only changes the things in its name: where we spend the money, the contact rate, contact to submit, or the number of agents. The first rows change one thing each, and the last row changes all three. A green tag means the row reaches your target, and a yellow tag means it does not.'} />,
      name: 'Path',
      width: 230,
      sticky: true,
      render: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          {/* The names run long on purpose ("Spend + contact rate + contact to
              submit"), so the column wraps rather than cutting one off. */}
          <Typography
            variant="body2"
            sx={{ fontWeight: r.today ? 600 : 400, whiteSpace: 'normal' }}
          >
            {r.label}
          </Typography>
          <StatusChip {...status(r)} />
        </Stack>
      ),
    },
    // The two numbers the targets above are set in, kept next to the path name
    // so they never need scrolling to.
    {
      key: 'cps',
      width: 130,
      sticky: true,
      header: <Heading label="Cost / submit" info={'What one submit costs us on this row: the spend divided by the number of submits. A submit is an application sent to the carrier. We work this out for every row, so it is not copied from your target. In brackets is how far it is from what a submit costs us today.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.cost_per_submit, today?.cost_per_submit ?? null,
        dollars(r.result.cost_per_submit), true),
    },
    {
      key: 'cpl',
      width: 110,
      sticky: true,
      header: <Heading label="Cost / lead" info={'What one new lead costs us on this row, on average: the spend divided by every lead we buy, Sales Lead or not. It never goes above the most you allow for one lead, set above, because we only give a campaign more money until its own leads reach that price.'} />,
      numeric: true,
      render: (r) => dollars(r.result.cost_per_lead),
    },
    // Then the row in words, and after it the figures behind it.
    {
      key: 'takes',
      header: <Heading label="What it takes" info={'The same row explained in words: what we would have to do, why that works, and what does not change. When a row cannot reach your target, it says why, and what a submit would still cost.'} />,
      name: 'What it takes',
      minWidth: 340,
      render: (r) => (r.today ? (
        <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary, whiteSpace: 'normal' })}>
          These are our figures today, for the dates and channels above.
        </Box>
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>{r.path?.summary}</Typography>
      )),
    },
    // Then the same reading order as the day-of-the-week table above: leads, MQLs, Sales Leads,
    // contacted, submits, conversion, spend, cost per lead, cost per submit.
    {
      key: 'agents',
      header: <Heading label="Agents" info={'How many agents this row needs, compared with the agents working today. More agents means each agent has fewer Sales Leads to call, and on past days, when agents had fewer Sales Leads each, more of the Sales Leads got a call. “More agents will not do it” means that hiring alone cannot get the contact rate high enough: on past days our agents never spoke with that many of the Sales Leads, even when each agent had very few Sales Leads to call.'} />,
      numeric: true,
      render: (r) => {
        if (r.today) return oneDecimal(r.result.agents);
        const need = r.path?.required;
        // Null means no number of agents delivers the contact rate this path
        // needs — past days never showed contact that high at any workload.
        if (!need || need.agents === null) {
          return (
            <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary, whiteSpace: 'normal' })}>
              More agents will not do it
            </Box>
          );
        }
        if (Math.abs(need.agents - (today?.agents ?? 0)) < 0.05) {
          return <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>Same as today</Box>;
        }
        return <ValueWithDelta value={need.agents} against={today?.agents} lowerIsBetter>{oneDecimal(need.agents)}</ValueWithDelta>;
      },
    },
    {
      key: 'leads',
      header: <Heading label="Leads / day" info={'How many new leads we buy a day on this row, across every campaign. Rows that move the spend usually buy fewer leads, because the money goes to the campaigns where more of the leads turn into submits, and those leads cost more each. In brackets is how far it is from the leads we buy today.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.leads_day, today?.leads_day ?? null, oneDecimal(r.result.leads_day)),
    },
    {
      key: 'mqls',
      header: <Heading label="MQLs / day" info={'How many of those leads are marked MQL, We\u2019re Sorry and Alt Mon included. When the spend moves, each campaign keeps today\u2019s share of MQLs among its leads. In brackets is how far it is from today.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.mqls_day, today?.mqls_day ?? null, oneDecimal(r.result.mqls_day)),
    },
    {
      key: 'sales_leads',
      header: <Heading label="Sales Leads / day" info={'How many of those leads are Sales Leads, i.e. marked MQL and not We\u2019re Sorry or Alt Mon. Agents only work Sales Leads, so the contact rate and contact to submit are over these. When the spend moves, each campaign keeps today\u2019s share of Sales Leads among its leads. In brackets is how far it is from today.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.sales_leads_day, today?.sales_leads_day ?? null, oneDecimal(r.result.sales_leads_day)),
    },
    {
      key: 'contact',
      header: <Heading label="Contact rate" info={'Of the Sales Leads we buy, the share our agents speak with, which means a call that lasted 90 seconds or more. When the number has a change in brackets, this row asks us to raise the contact rate to that level. When it is grey, this row does not ask anyone to change it: it moves only because buying fewer Sales Leads leaves each agent fewer to call.'} />,
      numeric: true,
      render: (r) => rate(r, r.result.contact, today?.contact, (r.path?.required.contact_lift ?? 1) > 1),
    },
    {
      key: 'submits',
      header: <Heading label="Submits / day" info={'How many applications we send to the carrier a day, on average. No row is allowed to send fewer than we send today, because a lower cost per submit that comes from writing less business does not help us.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.submits_day, today?.submits_day ?? null, twoDecimal(r.result.submits_day)),
    },
    {
      key: 'conversion',
      header: <Heading label="Contact to submit" info={'Of the Sales Leads our agents speak with, the share that send an application to the carrier. When the number has a change in brackets, this row asks us to raise contact to submit to that level. When it is grey, it stays as it is today.'} />,
      numeric: true,
      render: (r) => rate(r, r.result.conversion, today?.conversion, (r.path?.required.conversion_lift ?? 1) > 1),
    },
    {
      key: 'spend',
      header: <Heading label="Spend / day" info={'What we spend on buying leads in a day on this row, across every campaign. This is marketing spend only: what we pay our agents is not included anywhere in this table.'} />,
      numeric: true,
      render: (r) => vs(r, r.result.spend_day, today?.spend_day ?? null, dollars(r.result.spend_day), true),
    },
  ];


  const reachedCount = paths.filter((p) => p.reaches_goal).length;
  const headline = (() => {
    if (!today || !recommended || !ran) return null;
    // Plain spoken English (see "Writing" in ../../CLAUDE.md).
    const target = dollars(ran.cost_per_submit);
    const now = `Today a submit costs us ${dollars(today.cost_per_submit)}. Our agents speak with `
      + `${percent(100 * today.contact)} of the Sales Leads we buy, and ${percent(100 * today.conversion)} of those send an application.`;
    if (!reachedCount) {
      return `${now} None of the ${paths.length} ways below gets us to ${target} a submit while keeping every lead at `
        + `${dollars(ran.max_cost_per_lead)} or less and sending at least as many applications as today. The closest is `
        + `\u201c${recommended.label}\u201d, which gets us to ${dollars(recommended.result.cost_per_submit)} a submit.`;
    }
    const mix = paths.find((p) => p.key === 'mix');
    const mixText = mix && !mix.reaches_goal
      ? ` Moving the spend alone gets us to ${dollars(mix.result.cost_per_submit)} a submit, so to reach ${target} we `
        + 'will also have to change the contact rate, contact to submit or the number of agents.'
      : '';
    return `${now} ${reachedCount} of the ${paths.length} ways below get us to ${target} a submit, with no lead `
      + `costing more than ${dollars(ran.max_cost_per_lead)}.${mixText}`;
  })();

  return (
    <Panel
      title="Scenarios"
      subtitle="Every way we could reach the cost per submit you set above, one row each: what we would have to change, and what a submit would then cost."
      loading={loading && !payload}
      loadingHeight={320}
      footer={
        payload
          ? 'This is marketing spend only: what we pay our agents is not included yet.'
          : undefined
      }
    >
      <Stack spacing={2}>
        {/* Amber, the same as the calculator above says it: what the simulator
            cannot answer is almost always data that is not there yet, not a
            fault, and one message should never wear two colours on a page. */}
        {error && <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.warning })}>{error}</Typography>}

        {headline && (
          <>
            <Divider />
            <Typography variant="body1" aria-live="polite">{headline}</Typography>
          </>
        )}

        {today && (
          <DataTable
            ariaLabel="Paths to the target"
            columns={columns}
            groups={[{ key: 'paths', title: '', rows }]}
            getRowKey={(r) => r.key}
            collapsibleGroups={false}
            columnPicker={false}
          />
        )}


      </Stack>
    </Panel>
  );
}
